/* Vikas Tool Mart — product page info column behaviour
   Upload as: assets/vtm-product-info.js

   A custom element, deliberately, where vtm-product-card.js is a delegated
   document listener. The two live in different worlds:

     - cards get wiped and re-inserted by Shopify's filter AJAX, so a listener
       must sit ABOVE the swapped subtree;
     - this column is re-rendered whole by the theme editor every time a
       setting changes, so it needs per-instance setup AND teardown. A custom
       element gets both for free: the browser upgrades the new instance and
       calls disconnectedCallback on the old one, which is what stops the
       countdown interval leaking one timer per keystroke in the editor.

   The cart/drawer logic below mirrors vtm-product-card.js rather than
   importing it. That file has to keep working on collection pages where this
   asset is not loaded at all, so neither may depend on the other. */

(function () {
  if (customElements.get('vtm-product-info')) return;

  var DRAWER = 'cart-drawer, #cart-drawer, .cart-drawer, cart-notification, #CartDrawer';

  var TRIGGER = [
    '[data-cart-drawer-toggle]',
    '[data-drawer-open="cart"]',
    '.js-cart-drawer',
    '.header__icon--cart',
    'a[href$="/cart"]'
  ].join(', ');

  /* Refresh the drawer's CONTENTS in place, never replaceWith() — see the
     long note in vtm-product-card.js for why replacing the element made the
     drawer open only every other time. */
  async function refreshDrawer() {
    var live = document.querySelector(DRAWER);
    if (!live) return null;

    try {
      var wrapper = live.closest('[id^="shopify-section-"]');
      var url = wrapper
        ? location.pathname + '?section_id=' + encodeURIComponent(wrapper.id.replace('shopify-section-', ''))
        : location.pathname;

      var res = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!res.ok) return live;

      var doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      var fresh = doc.querySelector(DRAWER);
      if (!fresh) return live;

      live.innerHTML = fresh.innerHTML;
      return live;
    } catch (err) {
      console.warn('[vtm-pi] drawer refresh failed, opening stale drawer', err);
      return live;
    }
  }

  function openDrawer(drawer) {
    if (drawer && typeof drawer.open === 'function') { drawer.open(); return; }
    if (drawer && typeof drawer.show === 'function') { drawer.show(); return; }
    var trigger = document.querySelector(TRIGGER);
    if (trigger) trigger.click();
  }

  function pad(n) { return n < 10 ? '0' + n : String(n); }

  class VTMProductInfo extends HTMLElement {
    connectedCallback() {
      this.qtyInput = this.querySelector('[data-pi-qty]');

      /* The field ships with value="1", but a theme script that initialises
         quantity widgets store-wide can blank it before we get here — which
         reads as "the number is missing" and leaves the stepper starting
         from empty. Restore it rather than trusting the markup survived. */
      if (this.qtyInput && !this.qtyInput.value) {
        this.qtyInput.value = this.qtyInput.min || 1;
      }

      this.guardQtyVisibility();
      this.errorEl = this.querySelector('[data-pi-error]');
      this.variants = this.readVariants();

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);

      /* The share menu closes on an outside click or Escape, so these two
         have to sit on the document: a listener bound to this element never
         hears the click that lands anywhere else on the page. */
      this.onDocClick = this.onDocClick.bind(this);
      this.onDocKey = this.onDocKey.bind(this);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onDocKey);

      this.startClock();
      this.setupBar();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onDocKey);
      clearInterval(this.timer);
      if (this.observer) this.observer.disconnect();
      if (this.drawerObserver) this.drawerObserver.disconnect();
      if (this.barResize) this.barResize.disconnect();
      if (this.onScrollSettle) window.removeEventListener('scroll', this.onScrollSettle);
      if (this.applyBottomInset) window.removeEventListener('resize', this.applyBottomInset);
      /* Leave the theme's floats exactly as we found them. */
      this.liftFloats(false);
    }

    readVariants() {
      var tag = this.querySelector('[data-pi-variants]');
      if (!tag) return [];
      try {
        return JSON.parse(tag.textContent);
      } catch (err) {
        console.warn('[vtm-pi] variant JSON unreadable', err);
        return [];
      }
    }

    /* A theme rule can hide the quantity digit in a way no stylesheet of
       ours can undo: a vendor selector scoped by section id, carrying
       !important, outranks every class selector available to us. Tested —
       our !important rule loses that fight.

       So rather than escalate, measure. Read what actually computed, and
       only if the digit would be invisible, write the properties inline
       with !important, which nothing in the author origin can beat. Costs
       nothing on a healthy page: the check runs once and does nothing. */
    guardQtyVisibility() {
      var el = this.qtyInput;
      if (!el) return;

      var cs = getComputedStyle(el);
      var fill = cs.webkitTextFillColor || cs.color;
      var invisible =
        parseFloat(cs.opacity) === 0 ||
        parseFloat(cs.fontSize) < 6 ||
        parseFloat(cs.textIndent) < -100 ||
        /,\s*0\)$/.test(fill);

      if (!invisible) return;

      var ink = 'var(--pi-qty-color, #333333)';
      el.style.setProperty('color', ink, 'important');
      el.style.setProperty('-webkit-text-fill-color', ink, 'important');
      el.style.setProperty('font-size', '15px', 'important');
      el.style.setProperty('text-indent', '0', 'important');
      el.style.setProperty('opacity', '1', 'important');
    }

    /* ---------- countdown ---------- */

    startClock() {
      this.clock = this.querySelector('[data-pi-clock]');
      if (!this.clock) return;

      this.deal = this.clock.closest('[data-pi-deal]');

      /* Liquid emits a unix timestamp in seconds — unambiguous, where a
         formatted date would carry the SHOP's timezone and be read as the
         visitor's. Date.parse stays as a fallback for a hand-typed value. */
      var raw = this.clock.dataset.piClock;
      var end = /^\d+$/.test(raw) ? Number(raw) * 1000 : Date.parse(raw);
      if (isNaN(end)) { this.hideDeal(); return; }

      var tick = function () {
        var left = end - Date.now();

        /* The deal is over: drop the whole row rather than freezing it at
           zero, which is what the reference does on an expired flash sale. */
        if (left <= 0) {
          clearInterval(this.timer);
          this.hideDeal();
          return;
        }

        var s = Math.floor(left / 1000);
        this.setUnit('days', Math.floor(s / 86400));
        this.setUnit('hours', Math.floor(s / 3600) % 24);
        this.setUnit('minutes', Math.floor(s / 60) % 60);
        this.setUnit('seconds', s % 60);
      }.bind(this);

      tick();
      this.timer = setInterval(tick, 1000);
    }

    /* Scoped to the whole element, not to this.clock: the same unit appears
       twice on the page — once in the boxed countdown and once in the sticky
       bar — and both must tick off the one interval. */
    setUnit(name, value) {
      this.querySelectorAll('[data-pi-unit="' + name + '"]').forEach(function (el) {
        el.textContent = pad(value);
      });
    }

    hideDeal() {
      if (this.deal) this.deal.hidden = true;
      var barClock = this.querySelector('.vtm-pi__bar-clock');
      if (barClock) barClock.hidden = true;
    }

    /* ---------- sticky bar ---------- */

    setupBar() {
      this.bar = this.querySelector('[data-pi-bar]');
      if (!this.bar) return;

      /* The bar is redundant while the real buttons are on screen, so it
         only slides in once they have scrolled away. */
      var anchor = this.querySelector('.vtm-pi__actions');
      if (!anchor || !('IntersectionObserver' in window)) { this.showBar(true); return; }

      this.observer = new IntersectionObserver(function (entries) {
        this.showBar(!entries[0].isIntersecting);
      }.bind(this), { rootMargin: '0px 0px -40px 0px' });

      this.observer.observe(anchor);
      this.watchDrawer();

      this.applyBottomInset = this.applyBottomInset.bind(this);
      this.applyBottomInset();
      window.addEventListener('resize', this.applyBottomInset);
    }

    /* Glozin shows a fixed bottom nav bar on mobile, and this bar was
       landing underneath it. Rather than hard-code a height for one theme,
       ask the browser what is actually sitting at the bottom edge:
       elementsFromPoint returns the whole stack at a point, so the theme's
       bar shows up even while ours is painted over it.

       Anything taller than 40% of the viewport is ignored — that is a
       full-height drawer or overlay, not a nav strip. */
    applyBottomInset() {
      if (!this.bar) return;

      var inset = 0;
      if (document.elementsFromPoint) {
        var stack = document.elementsFromPoint(Math.round(window.innerWidth / 2), window.innerHeight - 2) || [];
        for (var i = 0; i < stack.length; i++) {
          var el = stack[i];
          if (el === this.bar || this.bar.contains(el) || el.contains(this.bar)) continue;

          var cs = getComputedStyle(el);
          if (cs.position !== 'fixed' && cs.position !== 'sticky') continue;

          var r = el.getBoundingClientRect();
          if (r.height > window.innerHeight * 0.4 || r.height < 8) continue;
          if (Math.abs(r.bottom - window.innerHeight) > 2) continue;

          inset = Math.round(r.height);
          break;
        }
      }

      this.bar.style.setProperty('--pi-bar-inset', inset + 'px');
    }

    showBar(show) {
      this.bar.classList.toggle('is-visible', show);
      this.liftFloats(show);
      if (show) { this.watchBarHeight(); this.watchLateFloats(); }
    }

    /* Floats can arrive AFTER the bar does, so one scan per show is not
       enough: Glozin's back-to-top is 0x0 until you scroll, and its
       "recently purchased" popup rotates in on a timer. Re-scan when
       scrolling settles rather than on every frame — the scan walks every
       element including shadow roots and is far too heavy to run per-frame. */
    watchLateFloats() {
      if (this.onScrollSettle) return;
      var self = this;
      var timer;
      this.onScrollSettle = function () {
        clearTimeout(timer);
        timer = setTimeout(function () {
          if (self.bar.classList.contains('is-visible')) self.liftFloats(true);
        }, 150);
      };
      window.addEventListener('scroll', this.onScrollSettle, { passive: true });
    }

    /* The bar's height is not settled the first time it shows: the countdown
       fills in on the next tick and a variant change can rewrap the price
       row, each of which moves the top edge after the floats were lifted —
       which is how the back-to-top button ended up 9px inside the bar again.
       Re-lift on a real size change rather than guessing at a delay. */
    watchBarHeight() {
      if (this.barResize || typeof ResizeObserver === 'undefined') return;
      var self = this;
      this.barResize = new ResizeObserver(function () {
        if (self.bar.classList.contains('is-visible')) self.liftFloats(true);
      });
      this.barResize.observe(this.bar);
    }

    /* How much room the bar is taking at the bottom of the viewport, written
       onto <html> so anything on the page can move out of its way in plain
       CSS: `bottom: calc(30px + var(--vtm-bar-lift, 0px))`.

       This exists for floats margin-bottom cannot reach. The WhatsApp widget
       is a third-party custom element whose button lives in a shadow root,
       and the app only lets you add CSS *inside* that root — where no
       selector can see `body` or this bar. Custom properties are the one
       thing that crosses a shadow boundary, because they inherit, so the
       widget can read this without knowing anything about the page.

       Kept in sync by every liftFloats() caller: show/hide, the bar's own
       ResizeObserver, and the scroll-settle rescan. */
    publishLift(px) {
      document.documentElement.style.setProperty('--vtm-bar-lift', Math.round(px) + 'px');
    }

    /* Glozin's WhatsApp bubble and back-to-top button are fixed to the
       bottom-right at an offset that lands inside this bar, so on a phone
       they sat on top of the price. Lift only the ones that actually
       overlap, by exactly the overlap, and put them back when the bar hides.

       Moved with margin-bottom, NOT transform. A fixed element with `bottom`
       set respects margin-bottom just the same, and themes animate floats
       with transform — borrowing that property meant a float with its own
       inline transform lost it the moment we put ours back. margin-bottom is
       one nothing else touches. */
    liftFloats(active) {
      if (!this.bar) return;

      if (!active) {
        (this.lifted || []).forEach(function (el) { el.style.marginBottom = ''; });
        this.lifted = [];
        this.publishLift(0);
        return;
      }

      var bar = this.bar;
      var found = [];

      /* Rescanned on every show, not cached: the back-to-top button is 0x0
         until you scroll, so a list built once would never contain it.

         Recurses into shadow roots — the WhatsApp widget on this store is a
         third-party custom element and its button lives inside a shadow
         root at z-index 2147483647. querySelectorAll does not cross that
         boundary, so a plain document scan finds nothing and the button
         goes on sitting over the price. */
      function scan(root) {
        [].forEach.call(root.querySelectorAll('*'), function (el) {
          if (el.shadowRoot) scan(el.shadowRoot);
          if (el === bar || bar.contains(el) || el.contains(bar)) return;
          if (getComputedStyle(el).position !== 'fixed') return;

          var r = el.getBoundingClientRect();

          /* Anything parked in the bottom band, on EITHER side. The old test
             only looked bottom-right, which is why Glozin's "recently
             purchased" popup went unnoticed: .fake-order is fixed bottom-LEFT
             at z-index 10 against this bar's 5, so it painted straight over
             the bar every time it rotated in and the bar read as randomly
             vanishing while scrolling. Raising the bar instead is not an
             option — the popup is 10 and Glozin's search overlay is already
             11, with the mini-cart at 15, so there is no free level between.

             A near-full-width strip is a bar, not a float: Glozin's mobile
             nav is one, and the bottom inset handles that. Tall elements are
             drawers and backdrops, which is-covered handles. */
          if (r.width >= window.innerWidth * 0.9) return;
          if (r.width <= 20 || r.height <= 20 || r.height > 220) return;
          if (r.bottom <= window.innerHeight - 320) return;
          found.push(el);
        });
      }
      scan(document.body);

      /* The bar's RESTING top, not its current one. showBar() calls this the
         instant the class flips, while the parked translateY(110%) is still
         animating out — getBoundingClientRect().top then reports an
         off-screen edge and every float measures as already clear. Layout
         height and the bottom offset are both immune to the transform. */
      var barBottom = parseFloat(getComputedStyle(bar).bottom) || 0;
      var barTop = window.innerHeight - bar.offsetHeight - barBottom;

      this.publishLift(bar.offsetHeight + barBottom);

      var previous = this.lifted || [];
      var next = [];

      found.forEach(function (el) {
        /* Measure where the float would sit UNLIFTED by adding back whatever
           we already put on it. Without this, a second pass measures its own
           previous correction, finds no overlap left, and drops the float
           from the list while its margin stays behind. */
        var applied = parseFloat(el.style.marginBottom) || 0;
        var bottom = el.getBoundingClientRect().bottom + applied;

        /* Clamped, because a float can never need lifting by more than the
           bar's own height — the worst case is one sitting flush with the
           bottom of the viewport. This is a safety net for a float that
           cannot actually be moved: if the margin has no effect the float
           measures unmoved on the next pass, and with this running on every
           scroll settle the correction would otherwise pile onto itself
           forever (74, 148, 222, 296 …). In normal operation it never binds. */
        var limit = bar.offsetHeight + 8;
        var overlap = Math.min(Math.round(bottom - barTop + 8), limit);
        if (overlap <= 0) return;
        if (applied !== overlap) el.style.marginBottom = overlap + 'px';
        next.push(el);
      });

      /* Release only what no longer needs holding up. Clearing every float
         and re-setting it on each pass made them visibly jump, now that this
         runs on every scroll settle rather than once per show. */
      previous.forEach(function (el) {
        if (next.indexOf(el) === -1) el.style.marginBottom = '';
      });

      this.lifted = next;
    }

    /* Lowering the bar's z-index gets it under most drawers, but not one
       whose own z-index is lower still — and Glozin's value is not something
       this file can know. So also step aside whenever the cart drawer is
       actually on screen.

       "Open" is decided by geometry, not by a class name: themes disagree on
       whether it is .active, .is-open, [open] or an inline transform, but
       they all agree that an open drawer occupies part of the viewport.
       MutationObserver is used rather than IntersectionObserver because it
       is microtask-driven and does not wait on a rendering update. */
    watchDrawer() {
      var drawer = document.querySelector(DRAWER);
      if (!drawer || !('MutationObserver' in window)) return;

      var sync = function () {
        var cs = getComputedStyle(drawer);
        var r = drawer.getBoundingClientRect();
        var onScreen =
          cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity) > 0 &&
          r.width > 0 && r.height > 0 &&
          r.right > 0 && r.left < window.innerWidth &&
          r.bottom > 0 && r.top < window.innerHeight;
        this.bar.classList.toggle('is-covered', onScreen);
      }.bind(this);

      this.drawerObserver = new MutationObserver(sync);
      this.drawerObserver.observe(drawer, {
        attributes: true,
        attributeFilter: ['class', 'style', 'open', 'aria-hidden', 'hidden']
      });
      sync();
    }

    /* ---------- variants ---------- */

    onChange(event) {
      if (!event.target.matches('[data-pi-variant-select]')) return;

      var id = Number(event.target.value);
      var variant = this.variants.find(function (v) { return v.id === id; });
      if (!variant) return;

      var idField = this.querySelector('[data-pi-variant-id]');
      if (idField) idField.value = variant.id;

      /* Every one of these appears twice — in the column and in the sticky
         bar — so each write has to hit both copies or the bar goes stale the
         moment someone picks a second variant. */
      this.fill('[data-pi-price]', variant.price);
      this.fill('[data-pi-sku]', variant.sku);

      /* Compare price and both Save badges travel together: a variant with
         no compare price must not keep the previous variant's discount. */
      this.fill('[data-pi-compare]', variant.compare_at_price);
      this.fill('[data-pi-save]', variant.save_label);
      this.fill('[data-pi-save-amount]', variant.save_amount);

      /* Retarget the label span, never the button itself — the Add to Cart
         button has an icon beside its text that textContent would eat. */
      this.querySelectorAll('[data-pi-submit]').forEach(function (btn) {
        btn.disabled = !variant.available;
        var label = btn.querySelector('[data-pi-btn-text]') || btn;
        label.textContent = variant.available ? btn.dataset.piLabel : btn.dataset.piSoldOut;
      });

      /* Keep the URL shareable, the same contract Shopify's own picker has. */
      var url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }

    /* Write a value to every copy of an element and hide the ones with
       nothing to say, so an empty compare price leaves no bare strikethrough
       behind. */
    fill(selector, value) {
      this.querySelectorAll(selector).forEach(function (el) {
        el.textContent = value || '';
        el.hidden = !value;
      });
    }

    /* ---------- quantity + buttons ---------- */

    onClick(event) {
      var step = event.target.closest('[data-pi-step]');
      if (step) {
        event.preventDefault();
        this.step(Number(step.dataset.piStep));
        return;
      }

      if (event.target.closest('[data-pi-more]')) {
        event.preventDefault();
        this.openFeatures();
        return;
      }

      if (event.target.closest('[data-pi-modal-close]')) {
        var open = this.querySelector('[data-pi-modal]');
        if (open) open.close();
        return;
      }

      /* Clicking the backdrop closes it. <dialog> reports backdrop clicks as
         clicks on the dialog itself, so the test is "outside the panel box"
         rather than a target check. */
      var modal = event.target.closest('[data-pi-modal]');
      if (modal && event.target === modal) {
        var box = modal.getBoundingClientRect();
        var outside = event.clientX < box.left || event.clientX > box.right ||
                      event.clientY < box.top || event.clientY > box.bottom;
        if (outside) modal.close();
        return;
      }

      var shareBtn = event.target.closest('[data-pi-share]');
      if (shareBtn) { event.preventDefault(); this.share(shareBtn); return; }

      var copyBtn = event.target.closest('[data-pi-share-copy]');
      if (copyBtn) { event.preventDefault(); this.copyLink(copyBtn); return; }

      /* A real share target closes the menu behind it and is then left alone
         to open its own tab. */
      if (event.target.closest('[data-pi-share-link]')) { this.closeShare(); return; }

      var buy = event.target.closest('[data-pi-buy]');
      if (buy) { event.preventDefault(); this.submit(buy, true); return; }

      var add = event.target.closest('[data-pi-add]');
      if (add) { event.preventDefault(); this.submit(add, false); }
    }

    /* Phones get the OS share sheet -- it already lists WhatsApp, and it is
       what a shopper expects this icon to do. Desktop gets our own menu:
       Chrome on Windows does expose navigator.share, but it opens a system
       sheet most people have no useful targets in, so the decision is a
       pointer test rather than a feature test. */
    share(btn) {
      var url = btn.dataset.shareUrl || location.href;

      if (typeof navigator.share === 'function' &&
          window.matchMedia('(hover: none)').matches) {
        navigator.share({
          title: btn.dataset.shareTitle || document.title,
          text: btn.dataset.shareText || '',
          url: url
        }).catch(function () { /* the shopper dismissed the sheet */ });
        return;
      }

      var menu = this.querySelector('[data-pi-share-menu]');
      if (!menu) return;

      if (!menu.hidden) { this.closeShare(); return; }
      menu.hidden = false;
      btn.setAttribute('aria-expanded', 'true');
      btn.classList.add('is-open');
    }

    closeShare() {
      var menu = this.querySelector('[data-pi-share-menu]');
      if (!menu || menu.hidden) return;
      menu.hidden = true;

      var btn = this.querySelector('[data-pi-share]');
      if (!btn) return;
      btn.setAttribute('aria-expanded', 'false');
      btn.classList.remove('is-open');
    }

    onDocClick(event) {
      if (this.contains(event.target)) return;
      this.closeShare();
    }

    onDocKey(event) {
      if (event.key !== 'Escape') return;

      var menu = this.querySelector('[data-pi-share-menu]');
      if (!menu || menu.hidden) return;

      this.closeShare();
      var btn = this.querySelector('[data-pi-share]');
      if (btn) btn.focus();
    }

    /* The execCommand fallback is kept on purpose: navigator.clipboard is
       undefined on any page not served over https, which is what a local
       test file is. */
    copyLink(btn) {
      var shareBtn = this.querySelector('[data-pi-share]');
      var url = (shareBtn && shareBtn.dataset.shareUrl) || location.href;
      var label = btn.querySelector('[data-pi-share-label]');

      function confirmed() {
        if (!label) return;
        if (!btn.dataset.idle) btn.dataset.idle = label.textContent;
        label.textContent = btn.dataset.copied || 'Link copied';
        btn.classList.add('is-done');
        clearTimeout(btn.resetTimer);
        btn.resetTimer = setTimeout(function () {
          label.textContent = btn.dataset.idle;
          btn.classList.remove('is-done');
        }, 1800);
      }

      function legacy() {
        var field = document.createElement('textarea');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
        document.body.appendChild(field);
        field.select();
        try { document.execCommand('copy'); confirmed(); }
        catch (err) { console.warn('[vtm-pi] copy failed', err); }
        document.body.removeChild(field);
      }

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(confirmed, legacy);
      } else {
        legacy();
      }
    }

    openFeatures() {
      var modal = this.querySelector('[data-pi-modal]');
      if (!modal) return;
      /* showModal, not show: only the modal form puts the dialog in the top
         layer with a backdrop, which is what keeps it above the sticky bar. */
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
    }

    step(delta) {
      if (!this.qtyInput) return;
      var min = Number(this.qtyInput.min) || 1;
      var next = Math.max(min, (Number(this.qtyInput.value) || min) + delta);
      this.qtyInput.value = next;
      var minus = this.querySelector('[data-pi-step="-1"]');
      if (minus) minus.disabled = next <= min;
    }

    async submit(btn, checkout) {
      if (btn.disabled || btn.classList.contains('is-busy')) return;

      var idField = this.querySelector('[data-pi-variant-id]');
      var id = idField && Number(idField.value);
      if (!id) return;

      btn.classList.add('is-busy');
      this.setError('');

      try {
        var res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: id, quantity: Number(this.qtyInput && this.qtyInput.value) || 1 }]
          })
        });

        /* Shopify answers 422 with a readable reason — usually "you can only
           add N to the cart". Show it instead of a generic failure. */
        if (!res.ok) {
          var body = await res.json().catch(function () { return {}; });
          throw new Error(body.description || body.message || 'Could not add to cart');
        }

        if (checkout) { window.location.href = '/checkout'; return; }

        var cart = await (await fetch('/cart.js')).json();

        document
          .querySelectorAll('.cart-count, [data-cart-count], .cart-count-bubble span, .header__cart-count')
          .forEach(function (el) { el.textContent = cart.item_count; });

        var drawer = await refreshDrawer();

        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cart } }));
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart: cart } }));

        await new Promise(function (r) { requestAnimationFrame(function () { r(); }); });
        openDrawer(drawer);
      } catch (err) {
        console.error('[vtm-pi]', err);
        this.setError(err.message);
      } finally {
        btn.classList.remove('is-busy');
      }
    }

    setError(message) {
      if (this.errorEl) this.errorEl.textContent = message;
    }
  }

  customElements.define('vtm-product-info', VTMProductInfo);

  /* ------------------------------------------------------------------
     Gallery — the left column of sections/vtm-main-product.liquid.

     Every image is in the DOM from the start and stepping just flips the
     `hidden` attribute, so there is no fetch between slides. The cost of
     that is paid for by `loading="lazy"` on all but the first image.
     ------------------------------------------------------------------ */

  class VTMProductGallery extends HTMLElement {
    connectedCallback() {
      this.slides = this.querySelectorAll('[data-pg-slide]');
      this.thumbs = this.querySelectorAll('[data-pg-go]');
      this.index = 0;

      this.onClick = this.onClick.bind(this);
      this.onKey = this.onKey.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKey);

      /* Swipe matters more than it looks: the thumbnail strip is hidden
         below 990px to match the reference, so on a phone the arrows would
         otherwise be the only way through the images. Passive listeners —
         this never calls preventDefault, so vertical page scrolling is
         untouched. */
      var stage = this.querySelector('.vtm-pg__stage');
      if (stage) {
        stage.addEventListener('touchstart', this.onTouchStart, { passive: true });
        stage.addEventListener('touchend', this.onTouchEnd, { passive: true });
        this.stage = stage;
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKey);
      if (this.stage) {
        this.stage.removeEventListener('touchstart', this.onTouchStart);
        this.stage.removeEventListener('touchend', this.onTouchEnd);
      }
    }

    onTouchStart(event) {
      var t = event.changedTouches[0];
      this.swipe = { x: t.clientX, y: t.clientY };
    }

    /* Only act on a clearly horizontal drag, so a diagonal scroll gesture
       does not also flick the gallery to the next image. */
    onTouchEnd(event) {
      if (!this.swipe) return;
      var t = event.changedTouches[0];
      var dx = t.clientX - this.swipe.x;
      var dy = t.clientY - this.swipe.y;
      this.swipe = null;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      this.go(this.index + (dx < 0 ? 1 : -1));
    }

    onClick(event) {
      var step = event.target.closest('[data-pg-step]');
      if (step) { this.go(this.index + Number(step.dataset.pgStep)); return; }

      var thumb = event.target.closest('[data-pg-go]');
      if (thumb) { this.go(Number(thumb.dataset.pgGo)); return; }

      if (event.target.closest('[data-pg-overview]')) { this.openZoom(); return; }

      if (event.target.closest('[data-pg-zoom-close]')) {
        var open = this.querySelector('[data-pg-zoom]');
        if (open) open.close();
        return;
      }

      var zoom = event.target.closest('[data-pg-zoom]');
      if (zoom && event.target === zoom) {
        var box = zoom.getBoundingClientRect();
        var outside = event.clientX < box.left || event.clientX > box.right ||
                      event.clientY < box.top || event.clientY > box.bottom;
        if (outside) zoom.close();
      }
    }

    /* Arrow keys move the gallery only when focus is already inside it, so
       they never hijack normal page scrolling. */
    onKey(event) {
      if (event.key === 'ArrowLeft') { this.go(this.index - 1); }
      else if (event.key === 'ArrowRight') { this.go(this.index + 1); }
      else { return; }
      event.preventDefault();
    }

    /* Wraps in both directions — the reference's arrows never dead-end. */
    go(next) {
      var count = this.slides.length;
      if (!count) return;

      this.index = ((next % count) + count) % count;

      this.slides.forEach(function (img, i) { img.hidden = i !== this.index; }, this);
      this.thumbs.forEach(function (btn, i) {
        btn.setAttribute('aria-current', i === this.index ? 'true' : 'false');
      }, this);
    }

    openZoom() {
      var zoom = this.querySelector('[data-pg-zoom]');
      if (!zoom) return;
      if (typeof zoom.showModal === 'function') zoom.showModal();
      else zoom.setAttribute('open', '');
    }
  }

  if (!customElements.get('vtm-product-gallery')) {
    customElements.define('vtm-product-gallery', VTMProductGallery);
  }
})();